import { Recipe } from '@/types/recipe';

export interface PageContext {
  type: 'home' | 'recipe-detail' | 'recipe-edit' | 'recipe-new' | 'search' | 'manage' | 'unknown';
  currentRecipe?: Recipe | null;
  isEditing?: boolean;
  isEmpty?: boolean;
  totalRecipes?: number;
  favoriteRecipes?: number;
}

export class ContextDetectionService {

  /**
   * Detect the current page context based on URL and recipe data
   */
  static detectPageContext(
    currentRecipe?: Recipe | null,
    totalRecipes: number = 0,
    favoriteRecipes: number = 0
  ): PageContext {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    // Recipe editing context
    if (path.includes('/recipe/new') || searchParams.has('from')) {
      return {
        type: 'recipe-new',
        isEditing: true,
        totalRecipes,
        favoriteRecipes
      };
    }

    // Recipe detail page
    if (path.includes('/recipe/') && currentRecipe) {
      const isEditMode = searchParams.has('edit') || path.includes('/edit');

      return {
        type: isEditMode ? 'recipe-edit' : 'recipe-detail',
        currentRecipe,
        isEditing: isEditMode,
        totalRecipes,
        favoriteRecipes
      };
    }

    // Management page
    if (path.includes('/manage')) {
      return {
        type: 'manage',
        totalRecipes,
        favoriteRecipes,
        isEmpty: totalRecipes === 0
      };
    }

    // Search results or home with search
    if (searchParams.has('search') || searchParams.has('q')) {
      return {
        type: 'search',
        totalRecipes,
        favoriteRecipes
      };
    }

    // Home page
    if (path === '/' || path === '/home' || path === '') {
      return {
        type: 'home',
        totalRecipes,
        favoriteRecipes,
        isEmpty: totalRecipes === 0
      };
    }

    // Unknown context
    return {
      type: 'unknown',
      totalRecipes,
      favoriteRecipes
    };
  }

  /**
   * Generate context-aware greeting based on page context
   * Occasionally includes helpful tips about capabilities
   */
  static generateContextualGreeting(context: PageContext): string {
    const { type, currentRecipe } = context;

    // Random tips to occasionally show (30% chance)
    const showTip = Math.random() < 0.3;

    // Context-specific tips
    const tips: Record<string, string[]> = {
      'recipe-detail': [
        '💡 Tip: Say "make this vegan" to get instant substitutions.',
        '💡 Tip: Try "scale to 6 servings" for easy adjustments.',
        '💡 Tip: Ask me to generate a food photo for this recipe!'
      ],
      'recipe-edit': [
        '💡 Tip: Paste messy recipe text — I\'ll clean it up instantly.',
        '💡 Tip: Say "convert to metric" for unit conversions.'
      ],
      'recipe-new': [
        '💡 Tip: Paste any recipe from a website and I\'ll format it.',
        '💡 Tip: Try the Recipe Studio for guided recipe creation!'
      ],
      'home': [
        '💡 Tip: Try the Recipe Studio (/studio) for AI-guided recipe creation.',
        '💡 Tip: Search naturally — like "something healthy with salmon".'
      ],
      'default': [
        '💡 Tip: I can create recipes, generate images, and answer cooking questions.'
      ]
    };

    const getRandomTip = (contextType: string): string => {
      const contextTips = tips[contextType] || tips['default'];
      return contextTips[Math.floor(Math.random() * contextTips.length)];
    };

    let greeting: string;

    switch (type) {
      case 'recipe-detail':
        greeting = currentRecipe
          ? `Hey! I see you're viewing "${currentRecipe.title}". Need help with substitutions, scaling, or an image?`
          : "Hey! I can help with this recipe — substitutions, scaling, or image generation.";
        break;

      case 'recipe-edit':
        greeting = currentRecipe
          ? `Editing "${currentRecipe.title}"? I can help with formatting, conversions, or dietary modifications.`
          : "I can help format, convert units, or make dietary modifications.";
        break;

      case 'recipe-new':
        greeting = "Creating something new? Paste any recipe text and I'll format it, or describe what you want to make!";
        break;

      case 'home':
        greeting = "👋 Hey! I can help you find recipes, create new ones, or generate food images. What sounds good?";
        break;

      case 'manage':
        greeting = "Need help organizing? I can help you find specific recipes or create new ones.";
        break;

      case 'search':
        greeting = "Try natural language search — like 'something spicy with chicken' or 'quick weeknight dinners'.";
        break;

      default:
        greeting = "👋 Hey! I can create recipes, generate images, or answer cooking questions. How can I help?";
    }

    // Occasionally append a tip
    if (showTip) {
      const tip = getRandomTip(type);
      greeting += `\n\n${tip}`;
    }

    return greeting;
  }

  /**
   * Get suggested quick actions based on context
   */
  static getContextualQuickActions(context: PageContext): string[] {
    const { type, isEmpty = false } = context;

    switch (type) {
      case 'recipe-detail':
        return [
          'Copy this recipe so I can modify it',
          'Generate an image for this recipe',
          'Make this recipe vegan',
          'Convert this to metric units'
        ];

      case 'recipe-edit':
        return [
          'Make this recipe vegan',
          'Convert this to metric units',
          'Clean up this recipe formatting',
          'Generate an image for this recipe'
        ];

      case 'recipe-new':
        return [
          'Help me structure this recipe',
          'Clean up this recipe text',
          'Create a recipe for chocolate chip cookies',
          'Generate an image when done'
        ];

      case 'home':
        if (isEmpty) {
          return [
            'Create a recipe for chocolate chip cookies',
            'Help me import a recipe from a website',
            'Find me some recipe inspiration',
            'Show me how to get started'
          ];
        }
        return [
          'Find me something with turmeric',
          'Create a new recipe',
          'Show me my favorite recipes',
          'Help me plan a meal'
        ];

      case 'manage':
        return [
          'Help me organize my recipes',
          'Find duplicate recipes',
          'Create a new recipe',
          'Export my recipe collection'
        ];

      case 'search':
        return [
          'Find me something with turmeric',
          'Show recipes for a romantic dinner',
          'What can I make with chicken?',
          'Find healthy breakfast recipes'
        ];

      default:
        return [
          'Create a new recipe',
          'Find me something with turmeric',
          'Help me with cooking questions',
          'Show me what you can do'
        ];
    }
  }
}

export default ContextDetectionService;